import {
    Elements,
    PaymentElement,
    useElements,
    useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import { STRIPE_PUBLIC_KEY } from '~/lib/Payments.js';
import { useContext, useState } from 'react';
import { ThemeContext } from '~/lib/ThemeContext';
import Loading from '~/components/Loading';
import usePublicPostApi from '~/public/lib/usePublicPostApi.js';
import useSharedPostApi from '~/lib/useSharedPostApi.js';
import * as Sentry from '@sentry/react';
import ErrorMessage from '~/components/ErrorMessage';
const stripeInstances = {};

function getStripe(accountId) {
    if (!accountId) {
        return null;
    }

    if (!stripeInstances[accountId]) {
        stripeInstances[accountId] = loadStripe(STRIPE_PUBLIC_KEY, {
            stripeAccount: accountId,
        });
    }

    return stripeInstances[accountId];
}

const StripeForm = ({
    returnUrl,
    paymentElementContainerClassName,
    paymentBtnLabel,
    type,
    id,
    paymentOptions,
    //isSetupIntent,
}) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const { save: saveStripePaymentIntent, isSaving } = useSharedPostApi(
        `/payments/stripe-payment-intent`,
    );

    const [errorMessage, setErrorMessage] = useState();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const themeContext = useContext(ThemeContext);
    const isThemed = !!themeContext && Object.keys(themeContext).length > 0;

    async function submit(ev) {
        ev.preventDefault();
        ev.stopPropagation();

        if (!stripe || !elements) {
            return;
        }

        setIsSubmitting(true);

        const { error: submitError } = await elements.submit();
        if (submitError) {
            setErrorMessage(submitError.message);
            setIsSubmitting(false);

            // Report Stripe element submission error to Sentry
            Sentry.withScope(scope => {
                scope.setContext('stripe_payment_error', {
                    error_type: 'stripe_elements_submit_failed',
                    error_code: submitError.code,
                    error_message: submitError.message,
                    error_type_detail: submitError.type,
                    intent_token: paymentOptions?.intentToken,
                });
                scope.setLevel('error');

                Sentry.captureMessage(`Stripe elements submit failed: ${submitError.message}`);
            });

            return;
        }

        let error = null;

        // TODO -- server side call to get payment intent!
        let paymentIntent;
        try {
            paymentIntent = await saveStripePaymentIntent({
                intentToken: paymentOptions?.intentToken,
            });
        } catch (err) {
            setErrorMessage('Failed to create payment intent. Please try again.');
            setIsSubmitting(false);

            // Report payment intent creation error to Sentry
            Sentry.withScope(scope => {
                scope.setContext('stripe_payment_error', {
                    error_type: 'stripe_payment_intent_creation_failed',
                    intent_token: paymentOptions?.intentToken,
                    error_message: err.response?.data?.message || err.message,
                    status_code: err.response?.status,
                    response_data: err.response?.data,
                });
                scope.setLevel('error');

                Sentry.captureException(err);
            });

            return;
        }

        const isSetupIntent = paymentIntent?.object === 'setup_intent';

        if (isSetupIntent) {
            ({ error } = await stripe.confirmSetup({
                //`Elements` instance that was used to create the Payment Element
                elements,
                clientSecret: paymentIntent.client_secret,
                confirmParams: {
                    return_url: returnUrl,
                },
            }));
        } else {
            ({ error } = await stripe.confirmPayment({
                //`Elements` instance that was used to create the Payment Element
                elements,
                clientSecret: paymentIntent.client_secret,
                confirmParams: {
                    return_url: returnUrl,
                },
            }));
        }

        setIsSubmitting(false);

        if (error) {
            // This point will only be reached if there is an immediate error when
            // confirming the payment. Show error to your customer (for example, payment
            // details incomplete)
            setErrorMessage(error.message);

            // Report Stripe confirmation error to Sentry
            Sentry.withScope(scope => {
                scope.setContext('stripe_payment_error', {
                    error_type: isSetupIntent ? 'stripe_setup_confirmation_failed' : 'stripe_payment_confirmation_failed',
                    error_code: error.code,
                    error_message: error.message,
                    error_type_detail: error.type,
                    payment_intent_id: paymentIntent?.id,
                    client_secret: paymentIntent?.client_secret ? '[REDACTED]' : null,
                    intent_token: paymentOptions?.intentToken,
                });
                scope.setLevel('error');

                Sentry.captureMessage(`Stripe ${isSetupIntent ? 'setup' : 'payment'} confirmation failed: ${error.message}`);
            });
        } else {
            // Your customer will be redirected to your `return_url`. For some payment
            // methods like iDEAL, your customer will be redirected to an intermediate
            // site first to authorize the payment, then redirected to the `return_url`.
        }
    }

    const isAdminApp = false;

    return (
        <form onSubmit={submit}>
            {isLoading && <Loading />}
            {!!error && (
                <ErrorMessage className="my-2">
                    <p>There was an error loading Stripe for checkout: {error}</p>
                    <p className="mt-2">If you are the owner of this business please log in to your Stripe dashboard to resolve the issue.</p>
                </ErrorMessage>
            )}
            <div className={paymentElementContainerClassName}>
                <PaymentElement
                    onLoaderStart={() => setIsLoading(true)}
                    onReady={() => setIsLoading(false)}
                    onLoadError={(e) => {
                        setError(e?.error?.message)
                        setIsLoading(false)
                    }}
                />
            </div>

            {!elements && (
                <div className="min-h-32 text-2xl">
                    <Loading />
                </div>
            )}

            {!!errorMessage && (
                <div className="text-red-600 my-2">{errorMessage}</div>
            )}
            <button
                className={`${!isThemed ? 'btn-save' : 'form-button'} mt-2`}
                disabled={!stripe}
            >
                {isSubmitting ? 'Paying...' : paymentBtnLabel || 'Pay Now'}
            </button>
        </form>
    );
};

const StripePaymentForm = ({
    type,
    id,
    paymentSettings,
    paymentBtnLabel,
    paymentElementContainerClassName,
}) => {
    const { color } = useContext(ThemeContext);

    let appearance = {};
    if (color) {
        appearance = {
            variables: {
                colorPrimary: color,
            },
        };
    }

    const options = {
        // pass the client secret from  the previous step
        //clientSecret: paymentSettings?.paymentIntent?.client_secret,
        ...(paymentSettings?.paymentIntent || {}),
        // Fully customizable with the Appearance API
        appearance: appearance,
    };

    return (
        <div key={paymentSettings?.paymentIntent?.amount}>
            <Elements
                stripe={getStripe(paymentSettings?.accountId)}
                options={options}
            >
                <StripeForm
                    returnUrl={paymentSettings?.returnUrl}
                    paymentBtnLabel={paymentBtnLabel}
                    paymentElementContainerClassName={
                        paymentElementContainerClassName
                    }
                    paymentOptions={paymentSettings?.paymentIntent}
                    type={type}
                    id={id}
                    // isSetupIntent={
                    //     paymentSettings?.paymentIntent?.object ===
                    //     'setup_intent'
                    // }
                />
            </Elements>
        </div>
    );
};

export default StripePaymentForm;
